import React, { type FC, useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { Grid, Link, type SxProps, type Theme } from '@mui/material'

import { isArrayNotEmpty } from '../../../utils'

import {
    canServiceBeDisabled,
    Capability,
    createIsAuthenticatedWithCapabilitiesSelector,
    type CybusService,
    selectDeletableLoadedServices,
    selectDisablableLoadedServices,
    selectServicesPage,
    type StatusType,
    Translation,
} from '../../../domain'

import { DetailsHeader, Hyphen, Status, useClearTableSelection } from '../common'
import { createFormatter, FormattedTranslation, useTranslator } from '../Internationalization'
import { RedirectingResourcesTable } from '../Resources'
import { RelativeRoutePathWithId, usePermissionedLink } from '../routing'
import { useAppState, useAppUsecase } from '../State'
import { AddOrUpdateModal } from './AddOrUpdate'
import { BulkDeletionModal, BulkDisablingModal } from './Bulk'
import { UploadServiceButton } from './Buttons'
import { ServiceLinks } from './Links'

const SNACKBAR_TIMEOUT = 3000

const Modals: FC = () => {
    const clear = useClearTableSelection()
    const { enqueueSnackbar } = useSnackbar()
    const PermissionedLink = usePermissionedLink()

    return (
        <>
            <BulkDeletionModal resourceSelector={selectDeletableLoadedServices} onConfirmed={clear} />
            <BulkDisablingModal resourceSelector={selectDisablableLoadedServices} onConfirmed={clear} />

            <AddOrUpdateModal
                mode="CREATE"
                onConfirmed={(id) =>
                    enqueueSnackbar(
                        <div key={id} data-testid="installed-service-item">
                            <Grid container justifyContent="space-between" spacing={6} alignItems="baseline">
                                <Grid data-testid="installed-service-name">{id}</Grid>
                                <Grid data-testid="installed-service-link">
                                    <PermissionedLink color="inherit" path={RelativeRoutePathWithId.SERVICE} id={id}>
                                        <FormattedTranslation id={Translation.MORE} />
                                    </PermissionedLink>
                                </Grid>
                            </Grid>
                        </div>,
                        { key: id, variant: 'info', autoHideDuration: SNACKBAR_TIMEOUT }
                    )
                }
            />
        </>
    )
}

const selectCanInstallServices = createIsAuthenticatedWithCapabilitiesSelector(
    Capability.SERVICES_PARAMETERS_READ,
    Capability.SERVICES_CREATE_OR_UPDATE,
    Capability.SERVICE_COMMISSIONING_FILE_READ
)
const selectCanManageServices = createIsAuthenticatedWithCapabilitiesSelector(Capability.SERVICES_MANAGE)

const buttonStyle: SxProps<Theme> = { cursor: 'pointer' }

const NoServicesMessage: FC = () => {
    const canInstallServices = useAppState(selectCanInstallServices)
    const addServiceUsecase = useAppUsecase('addServiceUsecase')
    const link = createFormatter(Link, { sx: buttonStyle, onClick: () => addServiceUsecase.startCreation() })

    return <FormattedTranslation id={Translation.NO_SERVICES_INSTALLED} values={{ canInstallServices, link }} />
}

const HeaderActions: FC = () => {
    const addService = useAppUsecase('addServiceUsecase')

    const canInstallServices = useAppState(selectCanInstallServices)

    const startServiceUpload = useCallback(() => addService.startCreation(), [addService])

    return (
        <>
            <UploadServiceButton onClick={startServiceUpload} disabled={!canInstallServices} />
        </>
    )
}

export const Table: FC = () => {
    const translation = useTranslator()
    const customCellRender = (status: StatusType): JSX.Element => <Status status={status} />

    return (
        <>
            <DetailsHeader
                title={[Translation.SERVICE, { count: 0 }]}
                titleActions={<HeaderActions />}
                description={[Translation.SERVICES_DETAILS_DOCUMENTATION_TITLE, Translation.SERVICES_DETAILS_DOCUMENTATION_BODY]}
            />
            <RedirectingResourcesTable<'manageServicesUsecase', CybusService, CybusService, 'bulkDeleteServicesUsecase' | 'bulkDisableServicesUsecase'>
                subscriptionUsecase="manageServicesUsecase"
                data={selectServicesPage}
                hasManagementCapabilitiesSelector={selectCanManageServices}
                dataTableMapper={(r) => r}
                columns={{
                    id: { label: Translation.SERVICE_ID, sort: true },
                    name: { label: Translation.NAME, sort: true },
                    version: { label: Translation.VERSION, sort: true },
                    links: {
                        label: translation.formatTranslation(Translation.LINK, { count: 2 }),
                        customCellRender: (links) => (isArrayNotEmpty(links) ? <ServiceLinks links={links} /> : <Hyphen />),
                    },
                    status: { label: Translation.STATUS, customCellRender, sort: true },
                    deviation: { label: Translation.DEVIATION, customCellRender },
                }}
                redirectOnRowclick={RelativeRoutePathWithId.SERVICE}
                selection={{
                    DELETE: { usecase: 'bulkDeleteServicesUsecase' },
                    DISABLE: { usecase: 'bulkDisableServicesUsecase', filter: canServiceBeDisabled },
                    resource: 'id',
                    usecaseMethod: 'selectServices',
                }}
                translations={{ emptyTable: <NoServicesMessage />, paginationDataType: 'SERVICE' }}
            >
                <Modals />
            </RedirectingResourcesTable>
        </>
    )
}
